import React, { useCallback } from "react"
import { GetApp } from "../App"
import type { IShip } from "../Types"
import { useEditState } from "../App/useEditState"
import { createStoreSettingsWriters, useCompanionShip, useDataStore } from "../Data"
import { EditGroup, EditGroupData } from "../Components/EditGroup"
import { Block } from "../Components/Block"
import { Loading } from "../Components/Loading"
import { TabBar } from "../Components/TabBar"
import { Overlay, OverlayTitle } from "../Components/Overlay"
import { OverlayScrollBody } from "../Components/OverlayScrollBody"
import { GetShipView } from "./Ship/Context"
import { logger, fieldNames } from "../App/Logger"
import { UnitsView } from "./ShipSettings/UnitsView"
import { TimezoneSettingView } from "./ShipSettings/TimezoneSettingView"
import { LogOffsetSettingView } from "./ShipSettings/LogOffsetSettingView"
import { KeepRpmSettingView } from "./ShipSettings/KeepRpmSettingView"
import { EditPermissivenessSettingView } from "./ShipSettings/EditPermissivenessSettingView"
import { CompanionsTab } from "./ShipSettings/CompanionsView"

export { ShipSettingsViewData } from "./ShipSettings/Data"

export interface ShipSettingsViewProps {
  ship: IShip;
  // Initial tab ("units"/"settings"/"companions"), matching the `initTab` convention
  // used by CruiseView/TripView/DayView/LogView/NMEAConfigurationView. Defaults to
  // "units" (the tab shown when opening the settings overlay normally); lets the
  // catalog render each tab directly instead of only ever showing "units".
  initTab?: string;
}

export function ShipSettingsView({ ship, initTab }: ShipSettingsViewProps) {
  const app = GetApp();
  const store = useDataStore();
  const t = app.getT("ShipSettingsView");
  const [edited, onEdited, resetEdited] = useEditState();
  const companion = useCompanionShip(store);
  const socketAddress = companion?.ip ? `${companion.ip}:${companion.port}` : "unknown";
  const settings = ship.shipSettings;
  const { resetShipOverlay } = GetShipView();
  const completedCallback = useCallback(() => resetShipOverlay(), []);

  const onEditEvent = useCallback((_event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    onEdited();
  }, [onEdited]);

  const save = useCallback(async (_data: FormData, values: EditGroupData) => {
    logger.debug("save", fieldNames(values));
    await createStoreSettingsWriters(store, { shipId: ship.id }).updateSettings(values);
    resetEdited();
  }, [store, ship.id, resetEdited]);

  if (settings === undefined) {
    return <Loading />;
  }

  return (
    <div className="ShipSettingsView">
      <Overlay closeCallback={completedCallback} className="ShipSettingsView-overlay overlay-max-height">
        <OverlayTitle closeCallback={completedCallback} title={t("ship_settings")} />
        <OverlayScrollBody className="Overlay-body" readMoreLabel={t("read_more")}>
          <TabBar
            defaultSelection={initTab || "units"}
            tabs={[
              { name: "units", title: t("tab_units"), render: () =>
                <UnitsView settings={settings} edited={edited} onEdited={onEdited} onEditEvent={onEditEvent} save={save} /> },
              { name: "settings", title: t("tab_settings"), render: () => (
                <EditGroup changed={edited} editable={true} editableFields={[{ name: "timezone", type: "int", default: null }, { name: "logOffset", type: "float", default: null }, { name: "keepManualRpm", type: "bool" }, { name: "editPermissiveness", type: "string", default: null }]} onSave={save}>
                  <Block className="tab-content block_field_53">
                    <TimezoneSettingView settings={settings} onEdited={onEdited} onEditEvent={onEditEvent} />
                    <LogOffsetSettingView settings={settings} onEditEvent={onEditEvent} />
                    <KeepRpmSettingView settings={settings} onEdited={onEdited} />
                    <EditPermissivenessSettingView settings={settings} onEdited={onEdited} />
                  </Block>
                </EditGroup>
              ) },
              { name: "companions", title: t("tab_companions"), render: () =>
                <CompanionsTab socketAddress={socketAddress} shipId={ship.id} /> },
            ]}
          />
        </OverlayScrollBody>
      </Overlay>
    </div>
  );
}
